// src/components/SwipeCard.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence, animate } from 'framer-motion';
import { extractEmailAddress, sanitizeHtml } from '../utils/sanitize';
import confetti from 'canvas-confetti';

export default function SwipeCard({
  email = {},
  onSwipe,
  forcedDirection,
  receiverEmail,
  innerRef,               // parent ref
  celebrateKey,           // optional backend-driven trigger
  celebrateTargetId,      // optional: which id should celebrate
  disabled = false,       // optional: block drag while submitting
}) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-20, 20]);
  const [visible, setVisible] = useState(true);

  const cardRef = useRef(null);
  const initialCenterRef = useRef({ x: 0.5, y: 0.5 });  // viewport-normalized center at mount
  const roRef = useRef(null);
  const firedImmediateRef = useRef(false);              // avoid double bursts when also using celebrateKey
  const prevCelebrateKeyRef = useRef(0);

  // Merge refs so parent can also access the card node
  const setNodeRef = useCallback((node) => {
    cardRef.current = node;
    if (innerRef) {
      if (typeof innerRef === 'function') innerRef(node);
      else innerRef.current = node;
    }
  }, [innerRef]);

  useEffect(() => {
   setVisible(true);
   firedImmediateRef.current = false;
   x.set(0);
 }, [email?.id]);

  // Capture the card's original (mounted) center and keep it fresh on resize
  useEffect(() => {
    const updateCenter = () => {
      const el = cardRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      initialCenterRef.current = {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      };
    };

    updateCenter();

    // Track size/position changes
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => updateCenter());
      ro.observe(document.documentElement);
      roRef.current = ro;
    }

    // Also update on window resizes/scroll (layout shifts)
    window.addEventListener('resize', updateCenter);
    window.addEventListener('scroll', updateCenter, true);

    return () => {
      if (roRef.current) roRef.current.disconnect();
      window.removeEventListener('resize', updateCenter);
      window.removeEventListener('scroll', updateCenter, true);
    };
  }, []);

  const reducedMotion = () =>
    !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  const fireConfettiAtInitialCenter = useCallback(() => {
    if (reducedMotion()) return;
    const origin = initialCenterRef.current || { x: 0.5, y: 0.5 };
    confetti({ particleCount: 70, spread: 70, startVelocity: 38, origin });
    confetti({ particleCount: 45, spread: 55, startVelocity: 26, origin, scalar: 0.9 });
  }, []);

  // Decide correctness client-side (best effort) for immediate feedback
  // Truth field may be named isPhishing / is_phishing / phishing
  const isCorrectLocal = useCallback((dir) => {
    const userAnswerIsPhishing = dir === 'left'; // ← flip to (dir==='right') if your mapping is opposite
const truth =
  typeof email.phishing === 'boolean' ? email.phishing :
  typeof email.isPhishing === 'boolean' ? email.isPhishing :
  typeof email.is_phishing === 'boolean' ? email.is_phishing :
  null;


    return truth === null ? null : (userAnswerIsPhishing === truth);
  }, [email]);

  // Handle a completed swipe
  const handleSwipeComplete = useCallback((dir) => {
    // Fire immediately if we can determine correctness from the email payload
    const localCorrect = isCorrectLocal(dir);
    if (localCorrect === true && !firedImmediateRef.current) {
      firedImmediateRef.current = true;     // mark so backend trigger won't double-fire
      fireConfettiAtInitialCenter();
    }

    // Notify parent (server is the real source of truth)
    if (onSwipe) onSwipe(dir, email);

    // Let parent control removal timing. If you want the card to also fade out here, keep this:
    setVisible(false);
  }, [email, onSwipe, isCorrectLocal, fireConfettiAtInitialCenter]);

  // Drag end threshold
  const handleDragEnd = (_evt, info) => {
    if (disabled) return; // block while submitting
    if (info?.offset?.x > 150) handleSwipeComplete('right');
    else if (info?.offset?.x < -150) handleSwipeComplete('left');
    else x.set(0);
  };

  // Forced swipe (buttons)
  useEffect(() => {
    if (!forcedDirection) return;
    const offset = forcedDirection === 'right' ? 300 : -300;
    const direction = forcedDirection;

    x.stop();
    animate(x, offset, {
      type: 'spring',
      stiffness: 150,
      damping: 20,
      onComplete: () => handleSwipeComplete(direction),
    });
  }, [forcedDirection, x, handleSwipeComplete]);

  // Backend-driven confetti (fallback) — only fire for the targeted id, and only once per key
  useEffect(() => {
    if (!celebrateKey) return;
    if (celebrateKey === prevCelebrateKeyRef.current) return; // only on change
    prevCelebrateKeyRef.current = celebrateKey;

    // Only celebrate if THIS card is the intended target
    if (email?.id == null || celebrateTargetId == null) return;
    if (email.id !== celebrateTargetId) return;

    // If we already fired immediately (client-side), skip to avoid duplicates
    if (firedImmediateRef.current) return;

    fireConfettiAtInitialCenter();
  }, [celebrateKey, celebrateTargetId, email?.id, fireConfettiAtInitialCenter]);

  const cleanBody = sanitizeHtml(email.content ?? email.body ?? '');
  const cleanFooter = sanitizeHtml(email.footer ?? '');

  const stopAnchorNav = (e) => {
    const t = e.target;
    if (t && t.tagName === 'A') e.preventDefault();
  };

  // If no email slipped through, render nothing
  if (!email || (!email.subject && !cleanBody)) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={setNodeRef}
          className="relative bg-[#e2e8f0] text-[#2b3b4b] p-6 rounded-xl shadow-xl max-w-[640px] w-full sm:w-[90%] md:w-[80%] lg:w-[640px]"
          style={{ x, rotate, pointerEvents: disabled ? 'none' : 'auto' }}
          drag={disabled ? false : 'x'}
          dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={handleDragEnd}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
        >
          <div>
            <h3 className="pb-4 text-xl font-semibold">{email.subject ?? ''}</h3>

            <div className="pb-4 text-sm text-gray-600 space-y-1">
              <div className="flex flex-wrap items-center text-sm text-gray-600">
                <strong className="mr-1">From:</strong>
                {email.department ? <span className="mr-2">{email.department}</span> : null}
                <span className="relative group inline-block">
                  <a
                    href={email.sender ? `mailto:${extractEmailAddress(email.sender)}` : '#'}
                    className="text-gray-500 hover:underline hover:text-[#00aca9]"
                    onClick={stopAnchorNav}
                  >
                    {email.sender ?? ''}
                  </a>
                  {email.sender ? (
                    <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-black text-white text-xs font-mono px-2 py-1 rounded shadow-lg z-10">
                      {email.sender}
                    </div>
                  ) : null}
                </span>
              </div>

              <p><strong>To:</strong> {receiverEmail ?? ''}</p>
            </div>

            <hr className="border-gray-300 w-full" />

            <div className="py-4" onClick={stopAnchorNav}>
              <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: cleanBody }} />
            </div>

            {cleanFooter.trim() && (
              <>
                <hr className="border-gray-300 w-full" />
                <div
                  className="text-xs text-gray-500 text-center pt-2"
                  onClick={stopAnchorNav}
                  dangerouslySetInnerHTML={{ __html: cleanFooter }}
                />
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
